import moment, {Moment} from 'moment-timezone'

import type {
  Electricity,
  ElectricityResponse,
  LatestPeakLoadWindow,
  PeakLoadWindow,
  PowerConsumption,
  EnergyConsumptionResponse,
  DatetimeValue
} from '../api'

export function sortByStart<T extends {start: string | Date | Moment}>(arr: T[]) {
  return arr.sort((a, b) => String(a.start).localeCompare(String(b.start)))
}

function checkPeakLoadWindowViolated(plw: PeakLoadWindow, powerConsumption: PowerConsumption[]) {
  const intervalViolatesPlw = powerConsumption.some((entry) => {
    const isIntervalInPeakLoadWindow = entry.dateTimeUTC.isBetween(
      plw.start,
      plw.end,
      undefined,
      '[)'
    )
    const isPowerViolated = entry.power > plw.maxPower
    return isIntervalInPeakLoadWindow && isPowerViolated
  })
  return intervalViolatesPlw
}

interface GetPeakLoadWindowsInput {
  peakLoadWindows: LatestPeakLoadWindow[]
  powerConsumption: PowerConsumption[]
}

export const getPeakLoadWindows = ({
  peakLoadWindows,
  powerConsumption
}: GetPeakLoadWindowsInput): PeakLoadWindow[] => {
  const plws = peakLoadWindows.map((plwDto) => ({
    maxPower: plwDto.max_power,
    start: moment.utc(plwDto.start),
    end: moment.utc(plwDto.end),
    violated: false
  }))

  return plws.map((plw) => ({
    ...plw,
    violated: checkPeakLoadWindowViolated(plw, powerConsumption)
  }))
}

export interface PowerDiffItem {
  hourStart: Moment
  mwDiff: number | null
}

type GetHourlyPowerDiff = (params: {
  hours: Moment[]
  plannedQuarterlyPower: PowerConsumption[]
  purchasedQuarterlyPower: PowerConsumption[]
}) => PowerDiffItem[]

const roundNumber = (
  num: number,
  decimals: number,
  mathClass: (x: number) => number = Math.round
): number => {
  if (decimals <= 0) {
    return mathClass(num)
  }
  const decimalCalc = 10 ** decimals
  return mathClass(num * decimalCalc) / decimalCalc
}

const groupPowerByHour = (powerList: PowerConsumption[]): Record<string, PowerConsumption[]> => {
  return powerList.reduce<Record<string, PowerConsumption[]>>((acc, item) => {
    const key = item.dateTimeUTC.clone().startOf('hour').toISOString()
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(item)
    return acc
  }, {})
}

/**
 * Calculates hourly deltas between purchased and planned power.
 * Baseload should be included in both planned and purchased arrays.
 */
export const getHourlyPowerDiff: GetHourlyPowerDiff = ({
  hours,
  plannedQuarterlyPower,
  purchasedQuarterlyPower
}) => {
  const plannedMWhByHour = groupPowerByHour(plannedQuarterlyPower)
  const purchasedMWhByHour = groupPowerByHour(purchasedQuarterlyPower)

  return hours.map((hourStart): PowerDiffItem => {
    const key = hourStart.toISOString()
    const purchased = purchasedMWhByHour[key]
    const planned = plannedMWhByHour[key]

    if (!purchased || !planned) {
      return {hourStart, mwDiff: null}
    }

    const totalProjectedMws =
      planned.reduce((total, item) => total + item.power, 0) / planned.length
    const totalPurchasedMws =
      purchased.reduce((total, item) => total + item.power, 0) / purchased.length

    return {hourStart, mwDiff: roundNumber(totalPurchasedMws - totalProjectedMws, 1)}
  })
}

export const mapEnergyConsumptionResToEnergyConsumption = (
  energyConsumptionResponse: EnergyConsumptionResponse[]
): PowerConsumption[] =>
  energyConsumptionResponse.map(({datetime, power}) => ({
    dateTimeIso: datetime,
    power,
    dateTimeUTC: moment.utc(datetime)
  }))

export const splitElectricityByDeadline = (
  submitted: Electricity['submitted'],
  nextPurchasingDeadline: string,
  timezoneId: string
) => {
  const startOfSubmission = moment
    .utc(nextPurchasingDeadline)
    .clone()
    .tz(timezoneId)
    .add(1, 'day')
    .startOf('day')
  return submitted.reduce<Pick<Electricity, 'purchased' | 'upForPurchasing'>>(
    (acc, item) => {
      if (item.dateTimeUTC.isBefore(startOfSubmission)) {
        acc.purchased.push(item)
      } else {
        acc.upForPurchasing.push(item)
      }
      return acc
    },
    {purchased: [], upForPurchasing: []}
  )
}

function ensureListSpacedIn15MinInterval(list: PowerConsumption[]) {
  const returnList: PowerConsumption[] = []
  for (let i = 0; i < list.length; i++) {
    if (i === list.length - 1) {
      returnList.push(list[i])
      break
    }
    for (
      let curTime = list[i].dateTimeUTC;
      list[i + 1].dateTimeUTC.isAfter(curTime);
      curTime = curTime.clone().add(15, 'minutes')
    ) {
      const dateTimeIso = curTime.toISOString()
      returnList.push(
        dateTimeIso === list[i].dateTimeIso
          ? list[i]
          : {
              dateTimeUTC: curTime,
              dateTimeIso: dateTimeIso,
              power: list[i].power
            }
      )
    }
  }
  return returnList
}

export const mapLoadCurvesResToElectricityData = (
  data: ElectricityResponse,
  timezoneId: string
): Electricity => {
  const planned = mapEnergyConsumptionResToEnergyConsumption(data.planned)
  const submitted = mapEnergyConsumptionResToEnergyConsumption(data.submitted)
  const nextPurchasingDeadline = data.nextPurchasingDeadline
  const lastSubmitted = data.lastSubmitted
  const {purchased, upForPurchasing} = splitElectricityByDeadline(
    submitted,
    nextPurchasingDeadline,
    timezoneId
  )
  return {
    planned: ensureListSpacedIn15MinInterval(planned),
    submitted: ensureListSpacedIn15MinInterval(submitted),
    purchased: ensureListSpacedIn15MinInterval(purchased),
    upForPurchasing: ensureListSpacedIn15MinInterval(upForPurchasing),
    nextPurchasingDeadline,
    lastSubmitted
  }
}

export const filterPeakLoadWindows = (
  data: PeakLoadWindow[] | undefined,
  startDatetime: Moment,
  endDatetime: Moment
): PeakLoadWindow[] => {
  if (!data) return []
  return data
    .filter(
      (peakLoadWindow) => peakLoadWindow.start < endDatetime && peakLoadWindow.end > startDatetime
    )
    .map(({start, end, ...rest}) => ({
      start: start > startDatetime ? start : startDatetime,
      end: end < endDatetime ? end : endDatetime,
      ...rest
    }))
}

interface MergeActualAndForecastInput {
  actual: DatetimeValue<Moment>[]
  forecast: DatetimeValue<Moment>[]
  startDatetime: Moment
  endDatetime: Moment
  timezoneId: string
}

/**
 * merges two datetime arrays of potentially overlapping times as follows
 * actual         aaa
 * forecast         bbb
 * result         aaabb
 * @param input as MergeActualAndForecastInput
 * @returns DatetimeValue<Moment>[]
 */

export const mergeActualAndForecast = ({
  actual,
  forecast,
  startDatetime,
  endDatetime,
  timezoneId
}: MergeActualAndForecastInput): DatetimeValue<Moment>[] => {
  const merged: DatetimeValue<Moment>[] = []

  let currentDate: Moment | null = null

  for (const entry of actual) {
    const datetimeInTimezone = entry.datetime.clone().tz(timezoneId)
    if (datetimeInTimezone < startDatetime) {
      continue
    }
    if (datetimeInTimezone > endDatetime) {
      break
    }
    merged.push({...entry})
    currentDate = datetimeInTimezone
  }

  for (const entry of forecast) {
    const datetimeInTimezone = entry.datetime.clone().tz(timezoneId)
    if (datetimeInTimezone < startDatetime) {
      continue
    }
    if (datetimeInTimezone > endDatetime) {
      break
    }
    if (currentDate && datetimeInTimezone <= currentDate) {
      continue
    }
    merged.push({...entry})
  }

  return merged
}
